import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  WizComponentManager,
  WizHgroup,
  WizIcon,
  WizEventManager,
  WizRoot
} from '@wizeoo/akm-component-lib';
import { BaselineEngine, VisionVO, WizConstants } from '@wizeoo/akm-compute-lib';
import { VisionStep } from '@wizeoo/akm-message-lib';

import { GridsterWidget } from '../../../vo/workingzone/gridster-widget';
import { AlkEvent } from '../../../framework/events/alk.event';
import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { GlobalFilterSettingPop } from './global-filter-settings.pop';
import { IWidgetFilter } from '../../../manager/widgets/i-widget';
import { WidgetBasic } from '../base/widget-basic';
import { PivotFilterExtendedRenderer } from '../pivot/renderers/filters/pivot-filter-extended.renderer';
import { getDefaultWidgetConfigurationObj, MirrorFilters } from './../../../manager/widgets/widget.engine';

// filtre global sur la vue
@Component({
  selector: 'global-filter-component',
  templateUrl: './global-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalFilter extends WidgetBasic implements AfterViewInit, OnDestroy {
 
  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  @ViewChild('hgDetailZone', { read: WizHgroup, static: true }) hgDetailZone: WizHgroup; // zone de filtre, pour écouteur sur setFilter
  @ViewChild('btnConf') btnConf: WizIcon; // btn settings
  @ViewChild('containerFilter', { read: ViewContainerRef, static: true }) containerFilter: ViewContainerRef; // zone d'ajout des champs de filtre

  /* ##############################################################################
                  paramètres spécifiques
  ############################################################################## */

  // les filtres sont visibles par l'utilisateur
  get showFilters(): boolean {
    return this.templatingMode || !!this._context?.showF;
  }

  public filterComponents: ComponentRef<any>[]; // les champs de filtre

  constructor(elementRef: ElementRef, private _cdRef: ChangeDetectorRef) {
    super(elementRef);
    this.widgetKind = WIDGET_KIND.GLOBAL_FILTER;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.BASELINE;
    this.settingsPopClassFactory = GlobalFilterSettingPop;
  }

  /* ##############################################################################
                    alimentation des données FEI / construction du grid
  ############################################################################### */

  /**
   * @override IWidget : méthode d'alimentation des données du composant
   * tout est à null, on continue quand même
   */
  public setDatasource(v: VisionVO, vs: VisionStep, scenario: string): void {
    
    this.vision = this.model.currentVision;
    try {
      this.visionStep = this.vision.visionSteps[BaselineEngine.getVisionStepCount(this.vision)];
      this.fei = this.model.dataEngine.generateFei(this.vision, this.visionStep, scenario, getDefaultWidgetConfigurationObj(), true); // et on le crée s'il n'existe pas
      
    } catch(e:any) {
      console.log('no vision on global', this.vision, this.vision.visionSteps);
    }

    this.drawContent();
  }


  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    
    // console.log('globalFilter', this.state, this.fei?.uniqueName, this.fei?.computed, origin);

    let mustBuildFilters: boolean = false; // on doit construire les filtres
    let mustFilter: boolean = false; // on doit appliquer les filtres

    // première création
    if(origin === WIDGET_ACTION_ORIGIN.INIT) {
      // alimentation du filtre global sur le widgetEngine
      let symbolic: GridsterWidget = this.model.widgetEngine.getWidgetByUid(this.uid);
      this.model.widgetEngine.feedGlobalFilter(symbolic); 
    }
    
    // première création
    if(origin === WIDGET_ACTION_ORIGIN.READY) {
      mustBuildFilters = true;
      mustFilter = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {   
      mustBuildFilters = true;
      mustFilter = true;
    } 
    
    // modification du filtre... on filtre, et on corrige éventuellement la sélection
    // en cas de champ utilisé en colonne, on reconstruit le grid
    if(origin === WIDGET_ACTION_ORIGIN.FILTERING) {
      mustFilter = true;
    }

    if(mustBuildFilters) {
      // construction du filtre logique
      this.buildFilters();
      // construction des filtres physiques
      this.buildFilterDrops();
    }

    if(mustFilter) {
      this.model.widgetEngine.refreshFilter(data); // reconstruction du filtre (symboliques)
      WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.GLOBAL_FILTER_UPDATE, data));
    }

    setTimeout(() => { // refresh visuel
      this._cdRef.markForCheck();
    }, 0);
  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   */
  public drawContent(): void {
    
    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN): void {
    this.drawOrRefresh(origin);
    super.alterContent(origin);
  }

  /**
   * interception de modification de filtre pour déclencher un refresh
   */
  protected innerUpdateFilter(e: AlkEvent): void {
    super.innerUpdateFilter(e);
    // console.log('gfc innerUpdateFilter', this.filters, e);
    
    if((e.data as IWidgetFilter).fkd?.isScen) { // et réalité, modification des scenarii
      // sur changement filtre Scenar, envoyer un ordre de refresh aux wigdet, ALK_EVENT_TYPE.GLOBAL_SCENAR_UPDATE
      // en cas de delta de scenar : WidgetEngine.submitGlobalScen(); 
      [...this.filters?.values()].forEach((filt: IWidgetFilter) => {
        if(filt.fkd.isScen) {
          let currentPile: string = [...this.model.widgetEngine.globalScenarii.values() || []]?.sort((a,b) => a.localeCompare(b))?.join(WizConstants.KEY_SEPARATOR) || 'null';
          let newPile: string = [...filt.values?.values() || []]?.sort((a,b) => a.localeCompare(b))?.join(WizConstants.KEY_SEPARATOR) || 'null';
          if(newPile !== currentPile) {
            // va dispatcher l'event de modif de global_scenar
            this.model.widgetEngine.submitGlobalScen(filt.values || new Set()); 
          }
        }
      });
    } else { // un vrai filtre
      this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING, e.data);
    }
}

/** suppression des combos de filtres (avant changement de step, ou sur retour de pop) */
private clearFilterDrops(): void {
  this.containerFilter.clear(); // vider le container
  this.filterComponents = []; // on rince la référence aux filtres
}

/** construction des dropdowns de filtre selon le contexte */
protected buildFilterDrops() {
  this.clearFilterDrops(); // on rince tout

  // construire uniquement le rendu, pas besoin d'alimenter les collections tant que la drop n'est pas ouverte
  if(this.filters) {
    [...this.filters.values()].forEach((filter: IWidgetFilter) => {
      const meCompo: ComponentRef<any> = WizComponentManager.instance.addChildTo(
        PivotFilterExtendedRenderer,
        this.containerFilter,
        { instanceProperties: { data: filter } }
      );
      this.filterComponents.push(meCompo);
    });

    if(!!this.context.targetPlace) {
      // envoi du pack pour synchro de la barre de menu
      let mirror: MirrorFilters = {widget: this, filters: this.filters, targetPlace: this.context.targetPlace};
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.GLOBAL_FILTER_INJECT, mirror));
    }

  }
  this._cdRef.markForCheck();
}

}
